import React, { useState, useMemo, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { ChevronLeft, ChevronRight, XLg, CalendarEventFill } from "react-bootstrap-icons";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  isValid
} from 'date-fns';
import { calender_icon } from "../../../assets/images";
import "../../DateTimePickerComponent/styles.css";

interface ServiceDateTimePickerProps {
  placeholder?: string;
  label: string;
  dateValue: string | null;
  timeValue: string | null;
  onDateChange: (date: string | null) => void;
  onTimeChange: (time: string | null) => void;
  className?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
  minTime?: string | null;
  maxTime?: string | null;
}

const ServiceDateTimePicker = ({
  placeholder,
  label,
  dateValue,
  timeValue,
  onDateChange,
  onTimeChange,
  className,
  minDate,
  maxDate,
  minTime,
  maxTime,
}: ServiceDateTimePickerProps) => {
  const [showModal, setShowModal] = useState(false);
  const [viewDate, setViewDate] = useState(dateValue ? new Date(dateValue) : new Date());
  const [tempDate, setTempDate] = useState<Date | null>(dateValue ? new Date(dateValue) : null);
  const [tempTime, setTempTime] = useState<string | null>(timeValue);

  useEffect(() => {
    if (showModal) {
      setTempDate(dateValue ? new Date(dateValue) : null);
      setTempTime(timeValue);
      if (dateValue) {
        setViewDate(new Date(dateValue));
      }
    }
  }, [showModal, dateValue, timeValue]);

  const handleOpen = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  const handleConfirm = () => {
    if (tempDate) {
      const year = tempDate.getFullYear();
      const month = String(tempDate.getMonth() + 1).padStart(2, '0');
      const day = String(tempDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      onDateChange(dateString);
      onTimeChange(tempTime);
    }
    handleClose();
  };

  const handleMonthPrev = () => setViewDate(subMonths(viewDate, 1));
  const handleMonthNext = () => setViewDate(addMonths(viewDate, 1));

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewDate));
    const end = endOfWeek(endOfMonth(viewDate));
    return eachDayOfInterval({ start, end });
  }, [viewDate]);

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const timeSlots = useMemo(() => {
    const isMinDay = minDate && tempDate && isSameDay(tempDate, minDate);
    const isMaxDay = maxDate && tempDate && isSameDay(tempDate, maxDate);
    const slots = [];

    // Full 24-hour range (00:00 to 23:45)
    for (let h = 0; h <= 23; h++) {
      for (let m = 0; m < 60; m += 15) {
        const value = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        let isDisabled = false;

        if (isMinDay && minTime && value < minTime) {
          isDisabled = true;
        }
        if (isMaxDay && maxTime && value > maxTime) {
          isDisabled = true;
        }

        slots.push({ displayTime: value, value, isDisabled });
      }
    }
    return slots;
  }, [tempDate, minDate, maxDate, minTime, maxTime]);

  const displayValue = useMemo(() => {
    if (!dateValue) return "";
    try {
      const d = new Date(dateValue);
      if (!isValid(d)) return "";
      const datePart = format(d, 'dd-MM-yyyy');
      return timeValue ? `${datePart} ${timeValue}` : datePart;
    } catch {
      return "";
    }
  }, [dateValue, timeValue]);

  return (
    <div className={` ${className || ""}`}>
      <Form.Label className="dashboard-label">{label}</Form.Label>
      <div
        className="dashboard-input d-flex align-items-center"
        onClick={handleOpen}
        style={{ cursor: 'pointer', height: 'auto', minHeight: '50px' }}
      >
        <CalendarEventFill size={18} className="me-2" style={{ color: 'var(--font-colour-theme-11)' }} />
        <span style={{ fontSize: '1.05rem', fontWeight: 500, color: displayValue ? '#fff' : 'rgba(255, 255, 255, 0.4)' }}>
          {displayValue || placeholder || "Select date & time"}
        </span>
      </div>

      <Modal show={showModal} onHide={handleClose} centered size="lg" className="custom-datetime-modal">
        <div className="custom-dt-header">
          <h5 className="custom-dt-title">Select {label}</h5>
          <button className="custom-dt-close" onClick={handleClose}><XLg size={18} /></button>
        </div>
        <div className="custom-dt-body">
          <div className="custom-dt-calendar-section">
            <div className="custom-dt-month-nav">
              <span className="custom-dt-current-month">{format(viewDate, 'MMMM yyyy')}</span>
              <div className="custom-dt-nav-btns">
                <button onClick={handleMonthPrev}><ChevronLeft size={16} /></button>
                <button onClick={handleMonthNext}><ChevronRight size={16} /></button>
              </div>
            </div>
            <div className="custom-dt-calendar-grid">
              {weekDays.map(day => <div key={day} className="custom-dt-weekday">{day}</div>)}
              {days.map((day, idx) => {
                const isCurrentMonth = isSameMonth(day, viewDate);
                const isSelected = tempDate && isSameDay(day, tempDate);
                const isMinOrPast = minDate ? (day < minDate && !isSameDay(day, minDate)) : (day < new Date() && !isToday(day));
                const isDisabled = !isCurrentMonth || isMinOrPast;

                return (
                  <button
                    key={idx}
                    className={`custom-dt-day ${!isCurrentMonth ? 'is-outside' : ''} ${isSelected ? 'is-selected' : ''} ${isToday(day) ? 'is-today' : ''} ${isDisabled ? 'is-past' : ''}`}
                    onClick={() => !isDisabled && setTempDate(day)}
                    disabled={isDisabled}
                  >
                    {format(day, 'd')}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="custom-dt-time-section">
            <div className="dt-info-box mb-4 p-3 text-center" style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '12px'
            }}>
              <label className="history-label d-block mb-2" style={{ fontSize: '0.7rem', opacity: 0.6 }}>SELECTED DATE & TIME</label>
              <div className="dt-info-value" style={{ color: '#f0b64f', fontWeight: 600, fontSize: '1.15rem' }}>
                {tempDate ? format(tempDate, 'dd MMM yyyy') : 'Please select a date'}
                {tempTime && <span className="ms-2" style={{ opacity: 0.8, fontSize: '1rem', fontWeight: 500 }}>at {tempTime}</span>}
              </div>
            </div>

            <div className="dt-time-picker-box">
              <label>Select Time (24h)</label>
              <div className="dt-time-grid">
                {timeSlots.map(slot => (
                  <button
                    key={slot.value}
                    className={`dt-time-btn ${tempTime === slot.value ? 'is-selected' : ''} ${slot.isDisabled ? 'is-past' : ''}`}
                    onClick={() => !slot.isDisabled && setTempTime(slot.value)}
                    disabled={slot.isDisabled}
                  >
                    {slot.displayTime}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="custom-dt-footer">
          <Button variant="link" onClick={handleClose} className="custom-dt-cancel">Cancel</Button>
          <Button onClick={handleConfirm} className="custom-dt-confirm">Confirm</Button>
        </div>
      </Modal>
    </div>
  );
};

export default ServiceDateTimePicker;
